import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import { useTranslation } from "react-i18next";

import { invoiceQueryKeys } from "../api/queryKeys";

import { instance } from "@/lib/axios/instance";
import {
  handleProblemDetailsError,
  ProblemDetails,
} from "@/lib/helpers/handleProblemDetailsError";
import useToast from "@/lib/hooks/useToast";
import { BulkCreateChannelInvoiceRequest } from "@/types/request/BulkCreateChannelInvoiceRequest";

type UseBulkCreateChannelInvoicesParams = {
  channelId: string;
  year: string;
  month: string;
  tenantId?: string | null;
};

/**
 * Creates multiple channel invoices in bulk for a specific channel, year, and month.
 * Automatically invalidates related queries and shows success/error toasts.
 */
export function useBulkCreateChannelInvoices(
  params: UseBulkCreateChannelInvoicesParams
) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { t } = useTranslation();

  return useMutation<
    AxiosResponse,
    AxiosError<ProblemDetails>,
    BulkCreateChannelInvoiceRequest
  >({
    mutationFn: (request: BulkCreateChannelInvoiceRequest) =>
      instance
        .post<AxiosResponse>(
          `/api/v2/Channels/${params.channelId}/Invoices/${params.year}/${params.month}/Bulk`,
          {
            ...request,
            tenantId: params.tenantId,
          }
        )
        .then((res) => res.data),
    onSuccess: () => {
      // Refetch the local page's data (no optimistic update on create, need server data)
      queryClient.invalidateQueries({
        queryKey: invoiceQueryKeys.cache.channelMonths.byChannelYearMonth(
          params.channelId,
          params.year,
          params.month
        ),
      });
      queryClient.invalidateQueries({
        queryKey: invoiceQueryKeys.channels.details(),
      });
      // Cross-page queries: mark stale, refetchOnMount: true handles refresh on navigation
      queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.cache.clientBudgetMonth.all() });
      queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.cache.clientChannels.all() });
      queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.cache.channels.all() });
      queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.cache.clients.all() });
      queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.cache.transactions.all() });
      showToast(t("success.save", { entity: t("common.invoices") }));
    },
    onError: (error) => {
      handleProblemDetailsError({
        error,
        t,
        showToast,
        fallbackMessage: t("errors.somethingWentWrong"),
      });
    },
  });
}
